import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import {
  createClearingTasks,
  getClearingAutomationSetting,
  getClearingTasks,
} from '~/javascripts/admin/actions/clearing';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { toQueryParams } from '~/javascripts/admin/utils';
import Button from '../../../features/common/components/Button';
import DateTime from '../../atoms/DateTime';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Required from '../../atoms/Required';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import ListTable from './ListTable';

type ClearingTasks = Awaited<ReturnType<typeof getClearingTasks>>;
type ClearingSetting = Awaited<ReturnType<typeof getClearingAutomationSetting>>;

type CreateFormData = {
  closing_date: Moment | null;
};

const PageWrapper = styled.div`
  ${Headings.h5}, ${Label}, ${DateTime} {
    margin-bottom: 0;
  }
`;

const ClearingFormWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1.25rem;
`;

const DateTimeWrapper = styled.div`
  width: 10rem;
`;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 20px 0 10px 0;
`;

const UnclickableButton = styled(Button)`
  pointer-events: none;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '精算管理', href: '', isCurrentPage: true },
  { value: '精算データ作成履歴', href: '', isCurrentPage: true },
];

const Clearing = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);

  const [clearingTasks, setClearingTasks] = useState<ClearingTasks>();
  const [currentPaginatedQuery, setCurrentPaginatedQuery] = useState<string | null>(null);
  const [apiMessages, setApiMessages] = useState<{ text: string; isError?: boolean }[]>([]);
  const [automationSetting, setAutomationSetting] = useState<ClearingSetting | null>(null);

  const allowedPrivateMoneys = user.issuedThirdPartyMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys.some((m) => m.id === privateMoneyId);

  const history = useHistory();

  const { handleSubmit, control } = useForm<CreateFormData>({
    defaultValues: {
      closing_date: moment().subtract(1, 'month').endOf('month'),
    },
  });

  const fetchClearingTasks = useCallback(
    (queryLink = '') => {
      if (privateMoneyId) {
        const queryString =
          queryLink ||
          toQueryParams(
            {
              private_money_id: privateMoneyId,
              per_page: 50,
            },
            true
          );

        getClearingTasks(queryString).then((res) => setClearingTasks(res));
      }
    },
    [privateMoneyId]
  );

  const onChangePage = (paginatedQuery: string | null) => {
    setCurrentPaginatedQuery(paginatedQuery);
    fetchClearingTasks(paginatedQuery ?? undefined);
  };

  const isValidClosingDate = (date: Moment) => {
    const dayOfMonth = date.date();
    const lastDayOfMonth = date.clone().endOf('month').date();
    return dayOfMonth === 15 || dayOfMonth === lastDayOfMonth;
  };

  const onConfirm = async (closingDate: string) => {
    try {
      await createClearingTasks({
        private_money_id: privateMoneyId!,
        closing_date: closingDate,
      });
      fetchClearingTasks(currentPaginatedQuery ?? undefined);
    } catch (err) {
      const errorData = await (err as Promise<{ message: string; type: string }>);
      setApiMessages([
        {
          text: errorData.message,
          isError: true,
        },
      ]);

      console.error(errorData);
    }
  };

  const onSubmit = (data: CreateFormData) => {
    if (!data.closing_date || !privateMoneyId) {
      return;
    }

    const closingDate = data.closing_date.toISOString();

    if (!isValidClosingDate(data.closing_date)) {
      dispatch(
        openAlertModal({
          body: <p>締め日が15日あるいは月末以外ですが実行しますか？</p>,
          confirmText: '実行',
          onConfirm: () => {
            onConfirm(closingDate);
          },
        })
      );
    } else {
      onConfirm(closingDate);
    }
  };

  useEffect(() => {
    fetchClearingTasks();
  }, [privateMoneyId, fetchClearingTasks]);

  useEffect(() => {
    getClearingAutomationSetting(privateMoneyId).then((setting) => {
      setAutomationSetting(setting);
    });
  }, [privateMoneyId]);

  // 手動作成の可否は「自動作成スケジュール(monthly_settlement_dates)」の有無だけで決める。
  // 自動相殺(auto_offset_enabled)はデータ作成とは独立した設定なので、有効でも手動作成は許可する。
  const settlementDates = automationSetting?.monthly_settlement_dates ?? [];
  const hasSchedule = settlementDates.length > 0;
  const autoOffsetEnabled = automationSetting?.auto_offset_enabled === true;
  // 表示ピル: スケジュール(自動作成設定中)と自動相殺の設定状態を併記する。
  const scheduleLabel = [
    settlementDates.includes(15) ? '毎月15日' : '',
    settlementDates.includes(0) ? '毎月末' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const settingLabel = [
    hasSchedule ? `自動作成設定中:${scheduleLabel ? ` ${scheduleLabel}` : ''}` : '',
    autoOffsetEnabled ? '自動相殺 有効' : '',
  ]
    .filter(Boolean)
    .join(' / ');

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {isSelectedMoneyAllowed ? (
        <>
          <ApiResults messages={apiMessages} />
          <Headings.h5>精算データ新規作成</Headings.h5>
          <SelectedPrivateMoneyDisplay />
          <ClearingFormWrapper>
            <Label>
              締め日 <Required />
            </Label>
            <DateTimeWrapper>
              <Controller
                name="closing_date"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <DateTime
                    locale="ja-JP"
                    dateFormat="YYYY/MM/DD"
                    timeFormat={false}
                    validType="invalidAfterToday"
                    value={value ?? undefined}
                    onChange={(date) => onChange(date ?? null)}
                    showIcon
                    inputProps={{
                      disabled: hasSchedule,
                    }}
                    wrapperStyles={{
                      opacity: hasSchedule ? '0.6' : '1',
                    }}
                  />
                )}
              />
            </DateTimeWrapper>
            <Button disabled={hasSchedule} onClick={handleSubmit(onSubmit)}>
              データ作成
            </Button>
            {settingLabel && (
              <UnclickableButton variant="secondary">{settingLabel}</UnclickableButton>
            )}

            <div style={{ marginLeft: 'auto' }}>
              <Button variant="secondary" onClick={() => history.push('/clearing/automation')}>
                自動作成設定
              </Button>
            </div>
          </ClearingFormWrapper>
          <HeadingWrapper>
            <Headings.h5>実行履歴</Headings.h5>
          </HeadingWrapper>
          <ListTable clearingTasks={clearingTasks} onChangePage={onChangePage} />
        </>
      ) : (
        <>
          <SelectedPrivateMoneyDisplay
            highlightHeader
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <UserPrivateMoneyNotAllowed />
        </>
      )}
    </PageWrapper>
  );
};

export default Clearing;
